import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useProgress } from "../progress";
import { usePoll, ErrorBanner, Loading, Stat, Section, Gauge, LaneBadge, StatusBadge, ExecBadges, SimBadge, polyLink, signed, f2, when, ago, minutesSince, useToast } from "./common";

function EquityCurve({ data }) {
  if (!data || data.length < 2) return <div className="equity-empty">Pas encore d'historique de capital</div>;
  const ys = data.map((d) => d.equity);
  const min = Math.min(...ys), max = Math.max(...ys);
  const W = 600, H = 190, pad = 6;
  const sx = (i) => pad + (i / (ys.length - 1)) * (W - 2 * pad);
  const sy = (v) => H - pad - ((v - min) / (max - min || 1)) * (H - 2 * pad);
  const path = ys.map((v, i) => `${i ? "L" : "M"}${sx(i).toFixed(1)},${sy(v).toFixed(1)}`).join(" ");
  const up = ys[ys.length - 1] >= ys[0];
  const color = up ? "var(--up)" : "var(--down)";
  const first = data[0]?.t, last = data[data.length - 1]?.t;
  return (
    <div>
      <svg className="equity" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        <path d={`${path} L${sx(ys.length - 1)},${H} L${sx(0)},${H} Z`} fill={color} opacity="0.08" />
        <path d={path} fill="none" stroke={color} strokeWidth="1.8" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="row-between tiny muted" style={{ marginTop: 6 }}>
        <span>{first ? new Date(first).toLocaleDateString("fr-FR") : ""} · min {min.toFixed(0)} $</span>
        <span>max {max.toFixed(0)} $ · {last ? when(last) : ""}</span>
      </div>
    </div>
  );
}

// Tout ce qui attend une action ou une confirmation : fills, ventes, gains à réclamer, signaux, erreurs CLOB.
function ToHandle({ queue, errors }) {
  if ((!queue || !queue.length) && (!errors || !errors.length)) return null;
  return (
    <Section title="À traiter" hint="ordres en attente de confirmation et dernières erreurs d'exécution">
      <div className="card">
        <table className="table">
          <tbody>
            {(queue || []).map((b) => (
              <tr key={b._id}>
                <td><SimBadge bet={b} /> <span className="q">{b.question} <span className="outcome">· {b.outcome}</span></span></td>
                <td><ExecBadges bet={b} />{b.status === "approved" && <span className="badge badge-warn">signal validé — à placer à la main ({f2(b.stake)} $)</span>}</td>
                <td className="right"><a href={polyLink(b)} target="_blank" rel="noreferrer" className="small nowrap">polymarket ↗</a></td>
              </tr>
            ))}
            {(errors || []).map((b) => (
              <tr key={b._id}>
                <td><SimBadge bet={b} /> <span className="q">{b.question} <span className="outcome">· {b.outcome}</span></span></td>
                <td><div className="down small">{b.rejectionReason}</div>{b.riskNotes && <div className="muted tiny">{b.riskNotes}</div>}</td>
                <td className="right muted tiny nowrap">{when(b.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

export default function Dashboard() {
  const { data: d, err, refresh } = usePoll(() => api("/dashboard"), 15000);
  const [syncing, setSyncing] = useState(false);
  const prog = useProgress();
  const toast = useToast();

  async function reconcile() {
    setSyncing(true);
    try { await api("/dashboard/reconcile", { method: "POST", body: {} }); toast("Capital resynchronisé sur le solde Polymarket."); refresh(); }
    catch (e) { toast(e.message, "down"); }
    finally { setSyncing(false); }
  }
  function scan() {
    prog.run({ path: "/agents/run", body: { type: "scan", maxDeep: 6 }, label: "Scan complet", onDone: refresh, onError: (m) => toast(m, "down") });
  }

  if (!d) return err ? <ErrorBanner err={err} /> : <Loading />;

  const live = d.mode === "live";
  const r = d.risk || {};
  const drift = d.local?.diffPct != null && Math.abs(d.local.diffPct) > 1;
  const tot = d.lanesTotal;
  const age = d.real?.fetchedAt ? minutesSince(d.real.fetchedAt) : null;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Tableau de bord</h1>
          <div className="sub">
            {live ? (d.live_trading_armed ? "Argent réel, ordres placés automatiquement" : "Mode réel non armé : signaux seulement") : "Simulation — aucun argent engagé"}
            {d.paused && <span className="badge badge-down" style={{ marginLeft: 8 }}>en pause{d.pause_reason ? ` · ${d.pause_reason}` : ""}</span>}
          </div>
        </div>
        <div className="page-actions">
          {live && <button className={"btn btn-sm " + (drift ? "btn-danger" : "btn-ghost")} onClick={reconcile} disabled={syncing} title="Aligne le cash suivi sur le solde Polymarket et ré-ouvre les positions clôturées à tort">
            {syncing ? "Synchro…" : drift ? `Resynchroniser (écart ${signed(d.local.diffPct, 1, " %")})` : "Resynchroniser"}
          </button>}
          <button className="btn btn-primary btn-sm" onClick={scan}>Lancer un scan</button>
        </div>
      </div>
      <ErrorBanner err={err} />

      {drift && <div className="notice notice-warn" style={{ marginBottom: 14 }}>Le cash suivi localement ({f2(d.local.cash)} $) s'écarte du solde Polymarket ({f2(d.real?.cash)} $) de plus de 1 %. Les garde-fous raisonnent sur un chiffre faux tant que ce n'est pas resynchronisé.</div>}

      <div className="stat-grid">
        <Stat label="Capital" value={f2(d.equity)} unit="$" cls={d.evolutionPct >= 0 ? "up" : "down"}
          sub={`${signed(d.evolutionPct, 1, " %")} depuis le départ (${f2(d.startBase ?? d.starting_bankroll)} $)${live && d.source === "polymarket" && age != null ? ` · lu il y a ${age} min` : ""}`} />
        <Stat label="Disponible" value={f2(d.cash)} unit="$" sub={(d.claimable || 0) > 0 ? `+ ${f2(d.claimable)} $ à réclamer` : "cash prêt à être engagé"} />
        <Stat label="Engagé" value={f2(d.exposureCost)} unit="$" sub={`${d.counts?.open ?? 0} position${(d.counts?.open ?? 0) > 1 ? "s" : ""} · valeur actuelle ${f2(d.exposureValue)} $`} />
        <Stat label="Aujourd'hui" value={signed(r.dayPnl)} unit="$" cls={(r.dayPnl || 0) >= 0 ? "up" : "down"}
          sub={tot ? `${tot.d1.n} clôture${tot.d1.n > 1 ? "s" : ""} sur 24 h · 7 jours : ${signed(tot.d7.pnl)} $ (${tot.d7.n})` : ""} />
      </div>

      <ToHandle queue={d.queue} errors={d.lastErrors} />

      <div className="two-col section">
        <div className="card card-pad">
          <div className="section-head"><h2>Évolution du capital</h2><span className="hint">{d.curve?.length || 0} relevés</span></div>
          <EquityCurve data={d.curve} />
        </div>
        <div className="card card-pad">
          <div className="section-head"><h2>Garde-fous</h2><span className="hint">base {f2(r.base)} $</span></div>
          <div className="col" style={{ gap: 14 }}>
            <Gauge label="Exposition" value={r.exposurePct} max={r.maxExposurePct} />
            <Gauge label="Drawdown depuis le pic" value={r.drawdownPct} max={r.maxDrawdownPct} hint={`pic ${f2(r.peak)} $`} />
            <Gauge label="Perte du jour" value={r.dayLossPct} max={r.maxDailyLossPct} />
            <Gauge label="Positions ouvertes" value={r.openCount} max={r.maxOpenPositions} suffix="" />
          </div>
        </div>
      </div>

      <Section title="Activité par voie" hint="positions ouvertes et résultats des clôtures" action={<Link to="/paris" className="small">voir les paris →</Link>}>
        <div className="card table-wrap">
          <table className="table">
            <thead><tr><th>Voie</th><th className="right">Ouverts</th><th className="right">24 h</th><th className="right">7 jours</th><th className="right">Total</th><th className="right">ROI</th><th className="right" title="Closing line value moyen : prix de clôture − prix d'entrée, en cents">CLV</th></tr></thead>
            <tbody>
              {(d.lanes || []).length === 0 && <tr><td colSpan={7} className="table-empty">Aucune activité pour l'instant.</td></tr>}
              {(d.lanes || []).map((l) => (
                <tr key={l.lane}>
                  <td><LaneBadge lane={l.lane} /></td>
                  <td className="right mono">{l.open}{l.open ? <span className="muted tiny"> · {f2(l.openStake)} $</span> : ""}</td>
                  <td className={"right mono " + (l.d1.n ? (l.d1.pnl >= 0 ? "up" : "down") : "muted")}>{l.d1.n ? `${signed(l.d1.pnl)} $` : "—"}{l.d1.n ? <span className="muted tiny"> ({l.d1.won}/{l.d1.n})</span> : ""}</td>
                  <td className={"right mono " + (l.d7.n ? (l.d7.pnl >= 0 ? "up" : "down") : "muted")}>{l.d7.n ? `${signed(l.d7.pnl)} $` : "—"}{l.d7.n ? <span className="muted tiny"> ({l.d7.won}/{l.d7.n})</span> : ""}</td>
                  <td className={"right mono " + (l.all.n ? (l.all.pnl >= 0 ? "up" : "down") : "muted")}>{l.all.n ? `${signed(l.all.pnl)} $` : "—"}{l.all.n ? <span className="muted tiny"> ({l.all.won}/{l.all.n})</span> : ""}</td>
                  <td className={"right mono " + (l.all.roi == null ? "muted" : l.all.roi >= 0 ? "up" : "down")}>{l.all.roi == null ? "—" : signed(l.all.roi, 1, " %")}</td>
                  <td className={"right mono " + (l.all.clv == null ? "muted" : l.all.clv >= 0 ? "up" : "down")}>{l.all.clv == null ? "—" : signed(l.all.clv, 1, "c")}{l.all.clvN ? <span className="muted tiny"> n={l.all.clvN}</span> : ""}</td>
                </tr>
              ))}
            </tbody>
            {tot && (d.lanes || []).length > 1 && (
              <tfoot><tr>
                <td>Total</td><td className="right mono">{tot.open}</td>
                <td className={"right mono " + (tot.d1.pnl >= 0 ? "up" : "down")}>{signed(tot.d1.pnl)} $</td>
                <td className={"right mono " + (tot.d7.pnl >= 0 ? "up" : "down")}>{signed(tot.d7.pnl)} $</td>
                <td className={"right mono " + (tot.all.pnl >= 0 ? "up" : "down")}>{signed(tot.all.pnl)} $</td><td /><td />
              </tr></tfoot>
            )}
          </table>
        </div>
      </Section>

      {d.lastClosed?.length > 0 && (
        <Section title="Dernières clôtures" action={<Link to="/paris" className="small">tout l'historique →</Link>}>
          <div className="card table-wrap">
            <table className="table">
              <tbody>
                {d.lastClosed.map((b) => (
                  <tr key={b._id}>
                    <td className="muted tiny nowrap">{ago(b.settledAt)}</td>
                    <td><LaneBadge lane={b.lane} /></td>
                    <td><span className="q">{b.question} <span className="outcome">· {b.outcome}</span></span></td>
                    <td><StatusBadge status={b.status} /></td>
                    <td className={"right mono " + ((b.pnl || 0) >= 0 ? "up" : "down")}>{signed(b.pnl)} $</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}
